(() => {
  const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
  const nonNegative = value => Number.isFinite(value) && value >= 0 && value <= Number.MAX_SAFE_INTEGER;
  const cents = value => Number.isSafeInteger(value) && value >= 0;
  const number = value => value === null || value === undefined || value === '' ? null : typeof value === 'number' || typeof value === 'string' ? Number(value) : NaN;
  const boolean = value => value === null || value === undefined || typeof value === 'boolean';

  function parse(value, fallback) {
    if (value === null || value === undefined || value === '') return fallback;
    return typeof value === 'string' ? JSON.parse(value) : value;
  }

  function id(value, type) {
    const raw = String(value ?? '');
    const match = raw.match(new RegExp(`^(?:gid://shopify/${type}/)?([1-9][0-9]*)$`));
    return match ? match[1] : null;
  }

  function calculate(input) {
    try {
      if (!record(input)) return null;
      const { cart, detail, organization } = input;
      if (!record(cart) || cart.currency !== 'USD' || !Array.isArray(cart.items)) return null;
      if (!record(detail) || !record(organization) || !Array.isArray(organization.sweepstakes)) return null;
      const giveawayId = id(detail.id, 'Sweepstake');
      if (!giveawayId) return null;
      const active = organization.sweepstakes.find(item => record(item) && id(item.id, 'Sweepstake') === giveawayId);
      if (!active || active.isActive !== true) return null;
      if (![active.bonusSpendingEnabledProductOnly, detail.productsAllowedByDefault, detail.calculateWithDiscount, detail.membershipProductMultOverride].every(boolean) || !Array.isArray(detail.products)) return null;
      const eligibleSpendingOnly = active.bonusSpendingEnabledProductOnly === true;
      const defaultAllowed = detail.productsAllowedByDefault ?? true;
      const calculateWithDiscount = detail.calculateWithDiscount === true;
      const membershipProductMultOverride = detail.membershipProductMultOverride === true;

      const management = parse(active.entryManagement, null);
      if (!record(management)) return null;
      const dollarSpent = number(management.dollarSpent);
      const entryPer = number(management.entryPer);
      if (!(dollarSpent > 0) || !nonNegative(dollarSpent) || !nonNegative(entryPer)) return null;
      if (management.roundUp !== undefined && typeof management.roundUp !== 'boolean') return null;
      const rounding = management.roundUp === undefined || management.roundUp ? Math.ceil : Math.floor;
      if (!boolean(detail.scheduledBonusActive)) return null;
      const scheduled = number(detail.scheduledBonusValue);
      if (detail.scheduledBonusActive && scheduled !== null && !nonNegative(scheduled)) return null;
      const rate = detail.scheduledBonusActive && scheduled > 0 ? scheduled : entryPer;
      const defaultBonus = number(detail.bonusEntriesDefault) ?? 0;
      if (!nonNegative(defaultBonus)) return null;

      const overrides = new Map();
      for (const product of detail.products) {
        if (!record(product)) return null;
        const productId = id(product.productId, 'Product');
        if (!productId || overrides.has(productId) || !boolean(product.productAllowed)) return null;
        const multiplier = number(product.productMultiplier) ?? 1;
        const bonus = number(product.productBonusEntries) ?? 0;
        const orderMultiplier = number(product.orderMultiplier) ?? 1;
        if (![multiplier, bonus, orderMultiplier].every(nonNegative)) return null;
        if (!boolean(product.orderMultiplierAllowed)) return null;
        overrides.set(productId, { allowed: product.productAllowed ?? true, multiplier, bonus, orderMultiplier, orderMultiplierAllowed: product.orderMultiplierAllowed === true });
      }

      const membership = parse(detail.membershipManagement, []);
      const tags = input.customerTags ?? [];
      if (!Array.isArray(membership) || !Array.isArray(tags) || !tags.every(tag => typeof tag === 'string')) return null;
      const customerTags = new Set(tags.map(tag => tag.toLowerCase()));
      let membershipBonus = 0;
      let membershipMultiplier = 1;
      for (const rule of membership) {
        if (!record(rule) || typeof rule.tag !== 'string') return null;
        const bonus = number(rule.bonusEntries) ?? 0;
        const multiplier = number(rule.multiplier) ?? 1;
        if (![bonus, multiplier].every(nonNegative)) return null;
        if (customerTags.has(rule.tag.toLowerCase())) {
          membershipBonus += bonus;
          membershipMultiplier = Math.max(membershipMultiplier, multiplier);
        }
      }

      const spendingRules = management.bonusSpending ?? [];
      if (!Array.isArray(spendingRules)) return null;
      for (const rule of spendingRules) {
        if (!record(rule) || typeof rule.spendingLimitEnabled !== 'boolean') return null;
        if (rule.spendingLimitEnabled && (![number(rule.spendingLimitDollar), number(rule.bonusEntries)].every(nonNegative))) return null;
      }

      const discountRules = parse(organization.discountCodes, {});
      const discountCodes = cart.discount_codes ?? [];
      if (!record(discountRules) || !Array.isArray(discountCodes)) return null;
      const discounts = new Map();
      for (const [code, rule] of Object.entries(discountRules)) {
        if (!record(rule)) return null;
        const entries = number(rule.entryGive) ?? 0;
        const multiplier = number(rule.multiplierGive) ?? 1;
        if (![entries, multiplier].every(nonNegative)) return null;
        discounts.set(code.toLowerCase(), { entries, multiplier: multiplier || 1 });
      }
      let selectedDiscount = null;
      for (const applied of discountCodes) {
        if (!record(applied) || typeof applied.code !== 'string') return null;
        const match = discounts.get(applied.code.toLowerCase());
        if (match && (!selectedDiscount || match.multiplier > selectedDiscount.multiplier || (match.multiplier === selectedDiscount.multiplier && match.entries > selectedDiscount.entries))) selectedDiscount = match;
      }
      selectedDiscount ??= { entries: 0, multiplier: 1 };

      const referralBonus = number(input.referralBonus) ?? 0;
      if (!nonNegative(referralBonus)) return null;
      let productEntries = 0;
      let eligibleSubtotalCents = 0;
      let itemCount = 0;
      let orderMultiplier = membershipProductMultOverride ? 1 : membershipMultiplier;
      for (const item of cart.items) {
        if (!record(item) || !Number.isSafeInteger(item.quantity) || item.quantity < 1) return null;
        const productId = id(item.product_id, 'Product');
        if (!productId || !cents(item.line_price) || !cents(item.original_line_price)) return null;
        itemCount += item.quantity;
        const override = overrides.get(productId);
        if (override?.orderMultiplierAllowed) orderMultiplier = Math.max(orderMultiplier, override.orderMultiplier);
        const allowed = override ? override.allowed : defaultAllowed;
        if (!allowed) continue;
        const linePrice = calculateWithDiscount ? item.line_price : item.original_line_price;
        const multiplier = membershipProductMultOverride ? membershipMultiplier : override?.multiplier ?? 1;
        const bonus = override ? override.bonus : defaultBonus;
        productEntries += rounding((rate / dollarSpent) * rounding(linePrice / 100)) * multiplier + bonus * item.quantity;
        eligibleSubtotalCents += linePrice;
      }
      if (!Number.isSafeInteger(itemCount) || !cents(eligibleSubtotalCents)) return null;
      if (cart.item_count !== undefined && cart.item_count !== itemCount) return null;
      const spendingSubtotalCents = eligibleSpendingOnly ? eligibleSubtotalCents : calculateWithDiscount ? cart.items_subtotal_price : cart.original_total_price;
      if (!cents(spendingSubtotalCents)) return null;
      let spendingBonus = 0;
      if (itemCount > 0) {
        for (const rule of spendingRules) {
          if (rule.spendingLimitEnabled && spendingSubtotalCents / 100 >= number(rule.spendingLimitDollar)) spendingBonus += number(rule.bonusEntries);
        }
      } else {
        membershipBonus = 0;
        selectedDiscount = { entries: 0, multiplier: 1 };
      }
      const appliedReferralBonus = itemCount > 0 ? referralBonus : 0;
      const total = (productEntries + spendingBonus + membershipBonus + selectedDiscount.entries + appliedReferralBonus) * orderMultiplier * Math.max(1, selectedDiscount.multiplier);
      const result = { total, productEntries, spendingBonus, membershipBonus, discountEntries: selectedDiscount.entries, referralBonus: appliedReferralBonus, orderMultiplier, discountMultiplier: Math.max(1, selectedDiscount.multiplier), eligibleSubtotalCents, spendingSubtotalCents };
      return Object.values(result).every(nonNegative) ? Object.freeze(result) : null;
    } catch {
      return null;
    }
  }

  globalThis.SacCartEntryRules = Object.freeze({ calculate });
})();
